import { useState, useEffect, useCallback, useRef } from "react";
import api from "../utils/api"; // ← your axios instance
import { getSocket } from "../socket/socket";

/**
 * useNotifications
 *
 * @param {string} userId  - The logged-in user's MongoDB _id string.
 *                           e.g. from useAuth(): const { user } = useAuth();
 *                                                <NotificationPanel userId={user?._id} />
 */
export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]); // always an array
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState("new"); // "new" | "all"
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const socketRef = useRef(null);

  // ── REST fetch ──────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(
    async (tab = "new", pageNum = 1) => {
      if (!userId) return;
      try {
        setLoading(true);

        // axios returns { data: { notifications, unreadCount, total, pages } }
        const { data } = await api.get("/notifications", {
          params: { tab, page: pageNum },
        });

        const items = Array.isArray(data.notifications)
          ? data.notifications
          : [];

        setNotifications((prev) =>
          pageNum === 1 ? items : [...prev, ...items],
        );
        setUnreadCount(data.unreadCount ?? 0);
        setHasMore(pageNum < (data.pages ?? 1));
        setPage(pageNum);
      } catch (err) {
        console.error("[useNotifications] fetch error:", err);
        // Keep existing state intact — don't reset to undefined
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  const markAsRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      const response = await api.patch(`/notifications/${id}/read`);
      // Socket sync — keeps other tabs up to date
      socketRef.current?.emit("notification:read", { notificationId: id });
    } catch (err) {
      console.error("[markAsRead] error:", err);
      // Roll back optimistic update on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: false } : n)),
      );
      setUnreadCount((c) => c + 1);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await api.patch("/notifications/read-all");
      socketRef.current?.emit("notification:read_all");
    } catch (err) {
      console.error("[markAllAsRead] error:", err);
      // Re-fetch to restore correct state
      fetchNotifications(activeTab, 1);
    }
  }, [activeTab, fetchNotifications]);

  const removeNotification = useCallback(
    async (id) => {
      const target = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (target && !target.read) setUnreadCount((c) => Math.max(0, c - 1));

      try {
        await api.delete(`/notifications/${id}`);
        socketRef.current?.emit("notification:delete", { notificationId: id });
      } catch (err) {
        console.error("[removeNotification] error:", err);
        // Re-fetch to restore
        fetchNotifications(activeTab, 1);
      }
    },
    [notifications, activeTab, fetchNotifications],
  );

  const loadMore = useCallback(() => {
    if (hasMore && !loading) fetchNotifications(activeTab, page + 1);
  }, [hasMore, loading, activeTab, page, fetchNotifications]);

  // ── Socket.io real-time events ──────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const socket = getSocket(userId);
    socketRef.current = socket;

    // New notification pushed from server
    socket.on("notification:new", (notif) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === notif.id)) return prev; // dedupe
        return [notif, ...prev];
      });
      setUnreadCount((c) => c + 1);
    });

    // Server confirmed one notification was read (multi-tab sync)
    socket.on("notification:read_ack", ({ notificationId }) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
    });

    // Server confirmed all-read (multi-tab sync)
    socket.on("notification:all_read", () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    });

    // Live unread count (keeps all tabs in sync)
    socket.on("notification:count", ({ unreadCount: c }) => {
      setUnreadCount(c);
    });

    // Server confirmed deletion
    socket.on("notification:deleted", ({ notificationId }) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    });

    return () => {
      socket.off("notification:new");
      socket.off("notification:read_ack");
      socket.off("notification:all_read");
      socket.off("notification:count");
      socket.off("notification:deleted");
    };
  }, [userId]);

  // ── Re-fetch when tab or user changes ───────────────────────────────────────
  useEffect(() => {
    fetchNotifications(activeTab, 1);
  }, [activeTab, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ─────────────────────────────────────────────────────────────────
  const displayed =
    activeTab === "new" ? notifications.filter((n) => !n.read) : notifications;

  const newCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    displayed,
    unreadCount,
    newCount,
    activeTab,
    setActiveTab,
    loading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    removeNotification,
    refetch: () => fetchNotifications(activeTab, 1),
  };
}
